// lib/offers/offerHtml.ts
// Browser-only. Fetches + caches a syfak "month-offer" HTML snippet by URL,
// on CLICK.
//
// The offer endpoint is the HOST's own (same-origin only when our widget is
// embedded on syfak's page), login-gated + per-customer — so this resolves REAL
// content only on the live embed; the dev mock stands in on localhost.
//
// ⛔ NO HOVER PREFETCH — removed 2026-07-28, and do not reintroduce it naively.
// It fired on every card `mouseenter`, and ~93% of syfak's priced products are
// offer cards, so simply sweeping the mouse across the grid toward a button
// queued one request per card passed over. Because the host takes ONE request at
// a time (hostQueue), the click then landed BEHIND that whole trail: the founder
// hit a popup that "took ages" on the live embed. The feature meant to make the
// popup instant was what made it slow — clicking with no hover history would
// have gone straight to the front of an empty queue.
//
// If it is ever wanted back, it needs all of: a dwell delay so a sweep queues
// nothing, at most ONE pending prefetch (newest replaces older), and dropping
// pending prefetches the moment a real click arrives. On a one-request-at-a-time
// host, speculative work spends the only lane on something probably unneeded.

// Cache the in-flight/settled PROMISE per URL, so repeat opens of the same offer
// share ONE request. Failures are evicted so a later open can retry rather than
// replaying a stale rejection.
import { enqueueHostRequest, hostKeyForUrl } from '@/lib/pricing/hostQueue';

const cache = new Map<string, Promise<string>>();

/** The offer endpoint is the HOST's, not our app — do NOT apiOrigin-prefix.
 *  Make a bare `index.php?…` host-root-relative. */
function resolveOfferUrl(offerUrl: string): string {
  if (/^https?:\/\//.test(offerUrl) || offerUrl.startsWith('/')) return offerUrl;
  return '/' + offerUrl;
}

/**
 * Defense-in-depth before injecting host HTML: drop executable/embeddable nodes.
 * innerHTML never runs <script> anyway; inline onclick handlers are KEPT — they
 * are the offer's own working stepper + add-to-cart (prosfores.add), and the
 * HTML comes from the host's own same-origin endpoint (the same trust boundary
 * as the host page our widget already runs inside).
 */
function cleanOfferHtml(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, iframe, object, embed, link, meta, base').forEach((el) => el.remove());
    return doc.body ? doc.body.innerHTML : html;
  } catch {
    return html;
  }
}

export function fetchOfferHtml(offerUrl: string): Promise<string> {
  const existing = cache.get(offerUrl);
  if (existing) return existing;
  const url = resolveOfferUrl(offerUrl);
  // SERIALISED with the pricing calls (2026-07-27) — same host, same session,
  // same one-request-at-a-time limit. Sending this straight into an in-flight
  // pricing batch is how a second request meets a busy host and comes back 503.
  // See lib/pricing/hostQueue.ts.
  const p = enqueueHostRequest(hostKeyForUrl(url), () =>
    fetch(url, { credentials: 'same-origin' }).then((r) => {
      if (!r.ok) throw new Error(`offer endpoint ${r.status}`);
      return r.text();
    }),
  )
    .then(cleanOfferHtml)
    .catch((err) => {
      cache.delete(offerUrl); // don't cache failures — allow a retry on next open
      throw err;
    });
  cache.set(offerUrl, p);
  return p;
}
